import React, { useEffect, useRef, useState } from 'react';
import type { IconComponent } from '../../icons';

interface BottomSheetProps {
  /** 열림 상태 */
  isOpen: boolean;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 제목 */
  title?: string;
  /** 시트 내용 */
  children: React.ReactNode;
  /** 높이 (기본: auto, 고정: 50vh, 75vh, full) */
  height?: 'auto' | '50vh' | '75vh' | 'full';
  /** 백드롭 클릭으로 닫기 허용 */
  closeOnBackdrop?: boolean;
  /** 드래그로 닫기 허용 */
  closeOnDrag?: boolean;
}

export function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  height = 'auto',
  closeOnBackdrop = true,
  closeOnDrag = true,
}: BottomSheetProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);

  // 열기/닫기 애니메이션
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // body 스크롤 방지
      document.body.style.overflow = 'hidden';
    } else {
      // 닫기 애니메이션 후 숨김
      const timer = setTimeout(() => setIsVisible(false), 300);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // 드래그 핸들러
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!closeOnDrag) return;
    startYRef.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!closeOnDrag || !isDragging) return;
    const diff = e.touches[0].clientY - startYRef.current;
    // 아래로 드래그만 허용
    if (diff > 0) {
      setTranslateY(diff);
    }
  };

  const handleTouchEnd = () => {
    if (!closeOnDrag) return;
    setIsDragging(false);

    // 50px 이상 드래그하면 닫기
    if (translateY > 50) {
      onClose();
    }
    setTranslateY(0);
  };

  const heightClass = {
    auto: 'max-h-[85vh]',
    '50vh': 'h-[50vh]',
    '75vh': 'h-[75vh]',
    full: 'h-[100vh]',
  }[height];

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[70]">
      {/* 백드롭 */}
      <div
        className={`
          absolute inset-0 bg-black transition-opacity duration-300
          ${isOpen ? 'opacity-50' : 'opacity-0'}
        `}
        onClick={closeOnBackdrop ? onClose : undefined}
      />

      {/* 시트 */}
      <div
        ref={sheetRef}
        className={`
          absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl
          ${heightClass}
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-y-0' : 'translate-y-full'}
        `}
        style={{
          transform: `translateY(${isOpen ? translateY : '100%'}px)`,
          transition: isDragging ? 'none' : undefined,
        }}
      >
        {/* 드래그 핸들 */}
        <div
          className="flex justify-center py-3 cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="w-12 h-1 bg-ink-300 rounded-full" />
        </div>

        {/* 헤더 */}
        {title && (
          <div className="px-5 pb-4 border-b border-line-soft">
            <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
          </div>
        )}

        {/* 내용 */}
        <div className={`overflow-y-auto ${title ? 'h-[calc(100%-60px)]' : 'h-[calc(100%-20px)]'}`}>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * 액션 시트 (메뉴형 바텀시트)
 */
export interface ActionSheetItem {
  id: string;
  label: string;
  /** 아이콘 컴포넌트(`components/icons`). 문자열 글리프는 받지 않는다 — `IconComponent` 주석 참조. */
  icon?: IconComponent;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

interface ActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  items: ActionSheetItem[];
}

export function ActionSheet({ isOpen, onClose, title, items }: ActionSheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={title} height="auto">
      <div className="px-2 pb-6">
        {items.map((item, index) => (
          <div key={item.id}>
            <button
              type="button"
              onClick={() => {
                if (!item.disabled) {
                  item.onClick();
                  onClose();
                }
              }}
              disabled={item.disabled}
              className={`
                w-full py-4 px-4 flex items-center gap-3 rounded-card
                transition-colors
                ${item.disabled
                  ? 'text-ink-300'
                  : item.danger
                    ? 'text-red-600 active:bg-red-50'
                    : 'text-ink-700 active:bg-ink-100'
                }
                ${index > 0 ? 'border-t border-line-soft' : ''}
              `}
            >
              {item.icon && <item.icon className="h-5 w-5 flex-shrink-0" />}
              <span className="text-base">{item.label}</span>
            </button>
            {item.disabled && item.disabledReason && (
              <p className="px-4 pb-2 text-xs text-ink-400">
                {item.disabledReason}
              </p>
            )}
          </div>
        ))}

        {/* 취소 버튼 */}
        <button
          type="button"
          onClick={onClose}
          className="w-full py-4 mt-4 text-ink-500 font-medium bg-ink-100 rounded-control"
        >
          취소
        </button>
      </div>
    </BottomSheet>
  );
}

export default BottomSheet;
